import React, {useEffect} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid,
    Select, MenuItem, FormControl, FormHelperText, OutlinedInput
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import * as Yup from 'yup';
import Close from '@material-ui/icons/CloseOutlined';
import Save from '@material-ui/icons/SaveOutlined';
import useFormularioInscricao from './useFormularioInscricao';
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import ContainedButton from '../../../shared/buttons/ContainedButton';
import TextButton from '../../../shared/buttons/TextButton';
import {DataNascimentoNumberFormatCustom} from "../../../shared/formatters";

const useStyles = makeStyles(theme => ({
    dialogContent: {
        paddingTop: theme.spacing(2)
    }
}));

const TIPO_ENSINO_OPTIONS = [
    {value: 'PU', label: 'Pública'},
    {value: 'PI', label: 'Privada com bolsa integral'},
    {value: 'PP', label: 'Privada com bolsa parcial'},
    {value: 'PS', label: 'Privada sem bolsa'},
    {value: 'P2', label: 'Pública / Privada'},
];

const initialForm = {
    formacaoId: '',
    dsCurso: '',
    instituicao: '',
    completo: 'N',
    dataConclusao: '',
    ensinoPublico: 'PU',
};

const validationSchema = Yup.object().shape({
    formacaoId: Yup.string().required('Formação é obrigatória'),
    dsCurso: Yup.string(),
    instituicao: Yup.string().trim().required('Instituição é obrigatória'),
    completo: Yup.string().required('Situação é obrigatória'),
    dataConclusao: Yup.string().when('completo', {
        is: 'S',
        then: Yup.string().required('Data de conclusão é obrigatória'),
        otherwise: Yup.string().nullable()
    }),
    ensinoPublico: Yup.string().required('Tipo de ensino é obrigatório')
});

export default function DialogEscolaridade({open, onClose, onSave, initialValues, formacoes}) {
    const classes = useStyles();

    const formDialogo = useFormularioInscricao({
        valoresIniciais: initialForm,
        schema: validationSchema,
        aoSubmeter: (valores) => {
            const formacaoSelecionada = (formacoes || []).find(f => String(f.id) === String(valores.formacaoId));
            onSave({
                formacao: formacaoSelecionada ? {id: formacaoSelecionada.id, descricao: formacaoSelecionada.descricao} : null,
                dsCurso: (valores.dsCurso || '').trim(),
                instituicao: valores.instituicao.trim(),
                completo: valores.completo,
                dataConclusao: valores.completo === 'S' ? valores.dataConclusao : null,
                ensinoPublico: valores.ensinoPublico,
            });
        }
    });
    const {valores, erros} = formDialogo;

    useEffect(() => {
        if (open) {
            formDialogo.reiniciar({
                values: initialValues ? {
                    formacaoId: initialValues.formacao?.id != null ? String(initialValues.formacao.id) : '',
                    dsCurso: initialValues.dsCurso || '',
                    instituicao: initialValues.instituicao || '',
                    completo: initialValues.completo || 'N',
                    dataConclusao: initialValues.dataConclusao || '',
                    ensinoPublico: initialValues.ensinoPublico || 'PU',
                } : initialForm
            });
        }
    }, [open, initialValues]);

    /* Em andamento não tem data de conclusão: limpa junto. */
    const aoMudarSituacao = (evento) => {
        formDialogo.aoMudar(evento);
        if (evento.target.value === 'N') {
            formDialogo.definirValor('dataConclusao', '');
        }
    };

    return (
        <Dialog scroll="body" open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{initialValues ? 'Editar Histórico Escolar' : 'Adicionar Histórico Escolar'}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container spacing={2}>
                    {/* Formação */}
                    <Grid item xs={12}>
                        <LabelValue label="Formação / Grau de Ensino *">
                            <FormControl variant="outlined" fullWidth error={!!erros.formacaoId}>
                                <Select
                                    name="formacaoId"
                                    value={valores.formacaoId}
                                    onChange={formDialogo.aoMudar}
                                    displayEmpty
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {(formacoes || []).map(f => (
                                        <MenuItem key={f.id} value={String(f.id)}>{f.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {erros.formacaoId && <FormHelperText>{erros.formacaoId}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {/* Curso */}
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Curso">
                            <FormControl variant="outlined" fullWidth error={!!erros.dsCurso}>
                                <OutlinedInput
                                    name="dsCurso"
                                    value={valores.dsCurso}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    inputProps={{maxLength: 100}}
                                />
                                {erros.dsCurso && <FormHelperText>{erros.dsCurso}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {/* Instituição */}
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Instituição *">
                            <FormControl variant="outlined" fullWidth error={!!erros.instituicao}>
                                <OutlinedInput
                                    name="instituicao"
                                    value={valores.instituicao}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    inputProps={{maxLength: 150}}
                                />
                                {erros.instituicao && <FormHelperText>{erros.instituicao}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {/* Completo */}
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Situação *">
                            <FormControl variant="outlined" fullWidth error={!!erros.completo}>
                                <Select
                                    name="completo"
                                    value={valores.completo}
                                    onChange={aoMudarSituacao}
                                >
                                    <MenuItem value="S">Concluído</MenuItem>
                                    <MenuItem value="N">Em Andamento</MenuItem>
                                </Select>
                                {erros.completo && <FormHelperText>{erros.completo}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {/* Data de Conclusão */}
                    <Grid item xs={12} sm={6}>
                        <LabelValue label={`Data de Conclusão${valores.completo === 'S' ? ' *' : ''}`}>
                            <FormControl variant="outlined" fullWidth error={!!erros.dataConclusao}>
                                <OutlinedInput
                                    name="dataConclusao"
                                    value={valores.dataConclusao}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    disabled={valores.completo !== 'S'}
                                    inputComponent={DataNascimentoNumberFormatCustom}
                                />
                                {erros.dataConclusao && <FormHelperText>{erros.dataConclusao}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {/* Tipo de Ensino */}
                    <Grid item xs={12}>
                        <LabelValue label="Tipo de Ensino que Cursou *">
                            <FormControl variant="outlined" fullWidth error={!!erros.ensinoPublico}>
                                <Select
                                    name="ensinoPublico"
                                    value={valores.ensinoPublico}
                                    onChange={formDialogo.aoMudar}
                                >
                                    {TIPO_ENSINO_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                                {erros.ensinoPublico && <FormHelperText>{erros.ensinoPublico}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose} startIcon={<Close />}>Fechar</TextButton>
                <ContainedButton onClick={formDialogo.submeter} startIcon={<Save />} disabled={formDialogo.enviando}>
                    Salvar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}
